import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Stethoscope, Eye, EyeOff, CheckCircle } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const portalApi = axios.create({ baseURL: '/api/family-portal' });

export default function PortalAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [form, setForm]     = useState({ password: '', confirm: '' });
  const [show, setShow]     = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone]     = useState(false);

  const submit = async e => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (form.password !== form.confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await portalApi.post('/portal/accept', { token, password: form.password });
      setDone(true);
      setTimeout(() => navigate('/portal/login'), 2500);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Invalid or expired invite link');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 shadow-xl mb-4">
            <Stethoscope size={26} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Rostery Family Portal</h1>
          <p className="text-slate-400 text-sm mt-1">Set up your account to get started</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {done ? (
            <div className="text-center py-4">
              <CheckCircle size={48} className="text-emerald-500 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-slate-800 mb-1">Account Activated!</h2>
              <p className="text-slate-500 text-sm">Redirecting you to login…</p>
            </div>
          ) : (
            <>
              <h2 className="text-lg font-bold text-slate-800 mb-1">Create your password</h2>
              <p className="text-sm text-slate-400 mb-6">Choose a secure password to access the family portal.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={show ? 'text' : 'password'}
                      value={form.password}
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                      placeholder="At least 8 characters"
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 pr-10"
                      required
                    />
                    <button type="button" onClick={() => setShow(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                      {show ? <EyeOff size={15}/> : <Eye size={15}/>}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">Confirm Password</label>
                  <input
                    type={show ? 'text' : 'password'}
                    value={form.confirm}
                    onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                    placeholder="Re-enter your password"
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400"
                    required
                  />
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm">
                  {loading ? 'Setting up…' : 'Activate Account'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
